import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid from '@mui/material/Grid';
import { useState } from 'react';

import Button, { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import {
  type Contentful,
  ContentStyle,
  type LinkContent,
} from '@/contentful/contentful-exports';

import FAQGroup from './FAQGroup/FAQGroup';
import useStyles from './GlobalFAQ.styles';

export interface AccordionContent extends Contentful {
  fields: {
    summary: string;
    details: RichTextDocument;
  };
}

export interface GlobalFAQContent extends Contentful {
  fields: {
    style?: ContentStyle;
    eyebrow?: string;
    heading?: string;
    faqs: AccordionContent[];
    footerText?: string;
    footerCta?: LinkContent;
  };
}

interface GlobalFAQProps {
  content: GlobalFAQContent;
}

export default function GlobalFAQ({
  content: {
    fields: { style, eyebrow, heading, faqs, footerText, footerCta },
  },
}: GlobalFAQProps) {
  const isOnDark = style === ContentStyle.OnDark;
  const { classes } = useStyles({ isOnDark });

  const [faqState, setFaqState] = useState<boolean[]>(
    Array(faqs.length).fill(false)
  );

  const faqsWithIndex = faqs.map((faq, accordionIndex) => ({
    ...faq,
    accordionIndex,
  }));
  const faqHalfIndex = Math.ceil(faqs.length / 2);

  return (
    <SpacingContainer className={classes.container}>
      <GridContainer fixed>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <SectionHeader
            eyebrow={eyebrow}
            heading={heading}
            isOnDark={isOnDark}
            fixed
          />
        </Grid>
      </GridContainer>
      <GridContainer fixed gridProps={{ className: classes.faqsContainer }}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FAQGroup
            faqs={faqsWithIndex.slice(0, faqHalfIndex)}
            faqState={faqState}
            setFaqState={setFaqState}
            isOnDark={isOnDark}
          />
        </Grid>
        <Grid
          className={classes.lastFaqSection}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FAQGroup
            faqs={faqsWithIndex.slice(faqHalfIndex)}
            faqState={faqState}
            setFaqState={setFaqState}
            isOnDark={isOnDark}
          />
        </Grid>
      </GridContainer>
      {(footerText || footerCta) && (
        <GridContainer fixed>
          <Grid className={classes.footer} size={12}>
            {footerText && <span>{footerText}</span>}
            {footerCta && (
              <Button
                label={footerCta?.fields.text}
                isOnDark={isOnDark}
                variant={ButtonVariant.SECONDARY}
                href={footerCta?.fields.url}
              />
            )}
          </Grid>
        </GridContainer>
      )}
    </SpacingContainer>
  );
}
